import axios from 'axios';
import { BASE_PATH_API, BASE_PATH_API_DEV, STAGE_DIRNAME, TIMEOUT_TIME } from 'constants/general';
import { decodeStorage, encodeStorage, removeStorage } from './storageBase64';

const BASE_URL = STAGE_DIRNAME === 'dev' 
    ? BASE_PATH_API_DEV?.creditoServices 
    : BASE_PATH_API;

const privateApi = axios.create({
    baseURL: BASE_URL,
    timeout: TIMEOUT_TIME,
    withCredentials: false,
    headers: {
        'Content-Type': 'application/json; charset=utf-8'
    }
});

const getCookieValue = (name) => {
    const match = document.cookie.match(new RegExp('(^| )' + name + '=([^;]+)'));
    return match ? match[2] : null;
};

privateApi.interceptors.request.use(
    (config) => {
        let token = decodeStorage('access_token');
        if (!token) {
            token = getCookieValue('access_token');
            if (token) {
                encodeStorage('access_token', token);
            }
        }
        if (token) {
            config.headers.Authorization = `Bearer ${token}`;
        }
        return config;
    },
    (error) => Promise.reject(error)
);

privateApi.interceptors.response.use(
    (response) => response,
    async (error) => {
        const originalRequest = error.config;
        const errorStatus = error.response?.status;
        
        if (!originalRequest) {
            return Promise.reject(error);
        }
        
        if (errorStatus === 401 && !originalRequest._retry) {
            originalRequest._retry = true;
            
            try {
                const refreshToken = decodeStorage('refresh_token');
                if (!refreshToken) {
                    throw new Error('No refresh token');
                }

                const response = await axios.get(`${BASE_PATH_API}/app/v1/login?refresh_token=${encodeURIComponent(refreshToken)}`, {
                    headers: { 'Content-Type': 'application/json' }
                });

                if (response.data?.access_token) {
                    const newToken = response.data.access_token;
                    const newRefreshToken = response.data.refresh_token;
                    
                    encodeStorage('access_token', newToken);
                    encodeStorage('refresh_token', newRefreshToken);
                    
                    const domain = window.location.hostname;
                    document.cookie = `access_token=${newToken};Max-Age=${60 * 60 * 24 * 365};Path=/;Domain=${domain};`;
                    document.cookie = `refresh_token=${newRefreshToken};Max-Age=${60 * 60 * 24 * 365};Path=/;Domain=${domain};`;
                    
                    originalRequest.headers.Authorization = `Bearer ${newToken}`;
                    return privateApi(originalRequest);
                }
            } catch (refreshError) {
                console.error('Token refresh failed:', refreshError);
                removeStorage('access_token');
                removeStorage('refresh_token');
                window.location.href = '/login';
                return Promise.reject(refreshError);
            }
        }
        
        if (!errorStatus || errorStatus === 0 || errorStatus >= 500) {
            console.warn('Service blocked or unavailable, will retry...', error.message);
            
            if (!originalRequest._retry) {
                originalRequest._retry = true;
                return new Promise((resolve) => {
                    setTimeout(() => {
                        resolve(privateApi(originalRequest));
                    }, 2000);
                });
            }
        }
        
        return Promise.reject(error);
    }
);

export const privateApiGet = async (url, config = {}) => {
    try {
        const response = await privateApi.get(url, config);
        return { 
            statusCode: response.status, 
            data: response.data, 
            error: '' 
        };
    } catch (error) {
        return {
            statusCode: error.response?.status || 0,
            data: error.response?.data || null,
            error: error.response?.data?.msg || error.message
        };
    }
};

export const privateApiPost = async (url, data, config = {}) => {
    try {
        const response = await privateApi.post(url, data, config);
        return { 
            statusCode: response.status, 
            data: response.data, 
            error: '' 
        };
    } catch (error) {
        return {
            statusCode: error.response?.status || 0,
            data: error.response?.data || null,
            error: error.response?.data?.msg || error.message
        };
    }
};

export default privateApi;
